import React from 'react'
import { FileUpload } from 'submission/components/deal-submission/file-upload'
import useFileUploader from 'hooks/useFileUploader'
import type { EquityFormData } from 'submission/lib/equity-form-defaults'

type Props = {
  form: EquityFormData
  setForm: React.Dispatch<React.SetStateAction<EquityFormData>>
  errors: Record<string, string>
}

const BasePdfsSection: React.FC<Props> = ({ form, setForm, errors }) => {
  const { uploadFile } = useFileUploader()

  const onUploadRow = (idx: number, file: File | null) => {
    if (!file) {
      setForm((p) => ({
        ...p,
        equityBasePdfs: p.equityBasePdfs.map((row, i) =>
          i === idx ? { ...row, key: '' } : row,
        ),
      }))
      return
    }
    uploadFile(
      { file, path: `submission/equity-base-${Date.now()}-${file.name}` },
      (res) =>
        setForm((p) => ({
          ...p,
          equityBasePdfs: p.equityBasePdfs.map((row, i) =>
            i === idx
              ? { ...row, key: res?.key ?? res?.url ?? res ?? '' }
              : row,
          ),
        })),
    )
  }

  const updateName = (idx: number, name: string) =>
    setForm((p) => ({
      ...p,
      equityBasePdfs: p.equityBasePdfs.map((row, i) =>
        i === idx ? { ...row, name } : row,
      ),
    }))

  const addRow = () =>
    setForm((p) => ({
      ...p,
      equityBasePdfs: [...p.equityBasePdfs, { name: '', key: '' }],
    }))

  const removeRow = (idx: number) =>
    setForm((p) => ({
      ...p,
      equityBasePdfs: p.equityBasePdfs.filter((_, i) => i !== idx),
    }))

  return (
    <section className="bg-white border border-slate-200 rounded p-6">
      <h2 className="text-lg font-semibold text-slate-900 mb-4">
        Base documents
      </h2>
      {form.equityBasePdfs.length === 0 && (
        <p className="text-slate-500 text-sm mb-3">
          Add at least one PDF that depositors will sign during shortlist.
        </p>
      )}
      <div className="flex flex-col gap-3">
        {form.equityBasePdfs.map((row, i) => {
          const currentFile = row.key
            ? new File([], row.name || 'document.pdf', {
                type: 'application/pdf',
              })
            : null
          return (
            <div
              key={i}
              className="border rounded p-3 flex flex-col md:flex-row md:items-end gap-3"
            >
              <label className="flex flex-col text-sm flex-1">
                <span className="text-slate-700 mb-1">Document name *</span>
                <input
                  className="border rounded px-2 py-1 text-slate-900 bg-white"
                  value={row.name}
                  onChange={(e) => updateName(i, e.target.value)}
                />
              </label>
              <div className="flex-1">
                <span className="text-sm text-slate-700 mb-1 block">PDF *</span>
                <FileUpload
                  mode="document"
                  accept="application/pdf"
                  buttonLabel={row.key ? 'Replace PDF' : 'Upload PDF'}
                  helperText="PDF, up to 25 MB"
                  maxSizeMB={25}
                  errorStrings={{
                    fileCount: 'Please upload one document.',
                    fileType: 'PDF only',
                    fileSize: 'Max 25 MB',
                    unreadable: 'File appears corrupted',
                    password: 'File appears protected',
                    empty: 'File is empty',
                    generic: 'Upload failed. Please try again.',
                    linkDropNotAllowed: 'Cannot drop a link',
                  }}
                  ariaLabel={`Upload base PDF ${i + 1}`}
                  onFileSelect={(file) => onUploadRow(i, file)}
                  currentFile={currentFile}
                  onRemove={() =>
                    setForm((p) => ({
                      ...p,
                      equityBasePdfs: p.equityBasePdfs.map((r, idx) =>
                        idx === i ? { ...r, key: '' } : r,
                      ),
                    }))
                  }
                />
              </div>
              <button
                type="button"
                className="px-3 py-1 border rounded text-sm"
                onClick={() => removeRow(i)}
              >
                Remove
              </button>
              {errors[`equityBasePdfs.${i}`] && (
                <span className="text-red-600 text-xs basis-full">
                  {errors[`equityBasePdfs.${i}`]}
                </span>
              )}
            </div>
          )
        })}
        <button
          type="button"
          className="self-start px-3 py-1 border rounded text-sm"
          onClick={addRow}
        >
          Add document
        </button>
        {errors.equityBasePdfs && (
          <span className="text-red-600 text-xs">{errors.equityBasePdfs}</span>
        )}
      </div>
    </section>
  )
}

export default BasePdfsSection
